import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Stack } from "@/core/components/ui/stack";
import {
  DocumentReviewActions,
  DocumentReviewPreview,
  readQueueReturnPosition,
  readInvoiceListReturnPath,
} from "@/accounts-payable/common";
import { useGetInvoice, useGetReviewQueue } from "@/accounts-payable/query";
import {
  AP_TAB,
  AP_TAB_LABEL,
  apListPath,
  flattenPages,
  invoiceReviewPath,
  parseApTab,
} from "@/accounts-payable/utils";
import { useBusiness } from "@/core/business";
import { InvoiceReviewHeader } from "./invoice-review-header";
import {
  findFirstNewQueueItem,
  getCarouselKeyboardDirection,
  ReviewQueueCarousel,
} from "./review-queue-carousel";

const AccountsPayableInvoicePage = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const { businessId } = useBusiness();
  const location = useLocation();
  const returnPosition = readQueueReturnPosition(location.state, businessId);
  const returnPath = readInvoiceListReturnPath(location.state, businessId);
  // Which tab opened this page, per `?tab=`. Absent means we can't know (direct
  // link, bookmark), which reads as "no list context" rather than a guess.
  const [searchParams] = useSearchParams();
  const fromTab = parseApTab(searchParams.get("tab"));
  const fromReviewQueue = fromTab === AP_TAB.review;

  const { data: invoice, isLoading } = useGetInvoice(businessId, invoiceId);
  // Carousel pills cover the review queue pages loaded so far; "next" past the
  // last pill fetches the next page. The carousel is designed to step through
  // the review queue; it doesn't render when arriving from any other tab.
  const queue = useGetReviewQueue(fromReviewQueue ? businessId : null);
  const { fetchNextPage: fetchNextQueuePage } = queue;
  const queueItems = useMemo(
    () => flattenPages(queue.data?.pages),
    [queue.data],
  );
  const orderedIds = useMemo(
    () => queueItems.map((item) => item.id),
    [queueItems],
  );

  const currentIndex = invoiceId ? orderedIds.indexOf(invoiceId) : -1;
  const prevId = currentIndex > 0 ? orderedIds[currentIndex - 1] : null;
  const nextId =
    currentIndex >= 0 && currentIndex < orderedIds.length - 1
      ? orderedIds[currentIndex + 1]
      : null;
  const canGoNext =
    !!nextId || (currentIndex === orderedIds.length - 1 && queue.hasNextPage);

  const goTo = useCallback(
    // Carry the source tab along, so stepping through the queue keeps both the
    // carousel and the back destination.
    (id: string) =>
      navigate(invoiceReviewPath(id, fromTab), { state: location.state }),
    [navigate, fromTab, location.state],
  );

  const goNext = useCallback(async () => {
    if (nextId) {
      goTo(nextId);
      return;
    }
    if (!canGoNext || queue.isFetchingNextPage) return;
    const result = await fetchNextQueuePage();
    // fetchNextPage resolves with the *existing* pages on error, and an
    // offset shift can make the new page open with already-loaded items —
    // only navigate to a genuinely new item, otherwise stay put.
    const nextItem = findFirstNewQueueItem(result.data?.pages, orderedIds);
    if (nextItem) goTo(nextItem.id);
  }, [
    nextId,
    canGoNext,
    queue.isFetchingNextPage,
    fetchNextQueuePage,
    goTo,
    orderedIds,
  ]);

  // Arrow keys flip between invoices, except while typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const direction = getCarouselKeyboardDirection(e.key, e.target);
      if (direction === "previous" && prevId) {
        e.preventDefault();
        goTo(prevId);
      }
      if (direction === "next" && canGoNext) {
        e.preventDefault();
        void goNext();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prevId, canGoNext, goNext, goTo]);

  // Changing invoice (incl. via the foot carousel) returns you to the top so
  // you start at the header, not wherever you'd scrolled to.
  const topRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    topRef.current?.scrollIntoView({ block: "start" });
  }, [invoiceId]);

  const back = (
    <button
      type="button"
      onClick={() =>
        navigate(
          returnPosition?.listPath ?? returnPath ?? apListPath(fromTab),
          {
            state: returnPosition
              ? { queueReturnPosition: returnPosition }
              : null,
          },
        )
      }
      className="flex w-fit items-center gap-1.5 text-sm text-tertiary-text transition-colors hover:text-primary-text"
    >
      <ArrowLeftIcon className="h-3.5 w-3.5" />
      {/* Without a source tab we land on the page's default tab, Review. */}
      Back to {AP_TAB_LABEL[fromTab ?? AP_TAB.review]}
    </button>
  );

  // Render the same moving-window control above and below the invoice. It only
  // shows previews for the visible segments, so a large queue stays compact.
  const carousel =
    // `currentIndex >= 0` guards a stale `?tab=review` link whose invoice has
    // since left the queue
    fromReviewQueue && currentIndex >= 0 && queueItems.length > 1 ? (
      <ReviewQueueCarousel
        items={queueItems}
        currentIndex={currentIndex}
        hasNextPage={queue.hasNextPage}
        isFetchingNextPage={queue.isFetchingNextPage}
        onSelect={goTo}
        onPrevious={() => prevId && goTo(prevId)}
        onNext={() => void goNext()}
      />
    ) : null;

  if (!invoice) {
    return (
      <Stack gap="md" className="mx-auto max-w-[1680px] px-8 py-6">
        {back}
        <span className="text-tertiary-text">
          {isLoading ? "Loading invoice…" : "Invoice not found."}
        </span>
      </Stack>
    );
  }

  return (
    <Stack ref={topRef} fullWidth className="mx-auto max-w-[1680px] px-8 pb-12">
      {/* Header — back link on the left, carousel centered on the same row,
          then a left-aligned title block sitting close to the content. */}
      <Stack gap="md" fullWidth className="pt-4">
        <Stack
          gap="none"
          fullWidth
          className="grid gap-3 lg:grid-cols-[1fr_auto_1fr] lg:items-center lg:gap-0"
        >
          <Stack gap="none" className="flex-1">
            {back}
          </Stack>
          {carousel && (
            <Stack gap="none" className="flex-none">
              {carousel}
            </Stack>
          )}
          <Stack gap="none" className="hidden flex-1 lg:flex" aria-hidden />
        </Stack>

        <InvoiceReviewHeader invoice={invoice} businessId={businessId} />
      </Stack>

      {/* PDF + actions */}
      <Stack
        gap="none"
        fullWidth
        className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_480px]"
      >
        <DocumentReviewPreview document={invoice} />

        <DocumentReviewActions
          documentId={invoice.id}
          documentType="invoice"
          documentHasJob={!!invoice.st_job_external_id}
          businessId={businessId}
        />
      </Stack>

      {/* Carousel repeated at the foot so you can advance without scrolling up. */}
      {carousel && (
        <Stack direction="row" justify="center" fullWidth className="pt-8">
          {carousel}
        </Stack>
      )}
    </Stack>
  );
};

export default AccountsPayableInvoicePage;
